import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setPageTitle } from "../../app/features/DataSlice";
import { useGetProfileQuery } from "../../app/features/apiSlice";
import { Card, Col, Row, Tab, Nav } from "react-bootstrap";
import Loader from "../../components/Loader";
import Error from "../../components/Error";
import HeliorPlanDetails from "../../components/HeliorPlan/HeliorPlanDetails";
import HOneDetails from "../../components/HeliorOne/HOneDetails";
import UserCard from "../../components/UserCard";
import PersonalDetails from "../../components/profileForm/PersonalDetails";
import ContactDetails from "../../components/profileForm/ContactDetails";
import BankDetails from "../../components/profileForm/BankDetails";
import SelectLanguage from "../../components/profileForm/SelectLanguage";
import RequestNewKey from "../../components/profileForm/RequestNewKey";
import ResidentialDetails from "../../components/profileForm/ResidentialDetails";
import DomicileDetails from "../../components/profileForm/DomicileDetails";

const Profile = () => {
  const pageTitle = "profile";
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sessionId = useRef(Date.now()).current;
  const { data, isLoading, isSuccess, isError, error, refetch } =
    useGetProfileQuery({
      sessionId,
    });

  useEffect(() => {
    dispatch(setPageTitle(t(`${pageTitle}`)));
    // eslint-disable-next-line
  }, [t]);

  return (
    <Row>
      {isLoading && <Loader />}
      {isError && <Error error={error} />}
      {isSuccess && (
        <>
          <Col lg={3}>
            <UserCard userData={data} />
          </Col>
          <Col lg={9}>
            <Card bg="dark1 text-light">
              <Card.Body>
                <Row className="py-3">

                  {/* Personal Data */}
                  <Col xs="12" lg="4">
                    <h6 className="text-silverBlue">
                      {t("signup.personalData")}
                    </h6>

                    <ul className="list-unstyled user-data my-3">
                      <li className="mobile-user-row">
                        <span className="text-uppercase">{t("email")}:</span>
                        <span className="text-end">{data?.user?.email}</span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("form.mobileNumber")}:
                        </span>
                        <span className="text-end">{data?.user?.phone}</span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("form.telephone")}:
                        </span>
                        <span className="text-end">{data?.orgUser?.telephone}</span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">{t("dob")}:</span>
                        <span className="text-end">
                          {data?.orgUser?.date_of_birth}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">{t("form.sex")}:</span>
                        <span className="text-end">{data?.orgUser?.sex}</span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("form.typology")}:
                        </span>
                        <span className="text-end">{data?.orgUser?.type}</span>
                      </li>

                      {data?.orgUser?.type === "COMPANY" && (
                        <li className="mobile-user-row">
                          <span className="text-uppercase">
                            {t("form.companyName")}:
                          </span>
                          <span className="text-end">
                            {data?.user?.orgUser?.company_name}
                          </span>
                        </li>
                      )}

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("form.taxId")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.tax_code}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("form.pec")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.PEC}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("form.affiliateCode")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.affiliate_code}
                        </span>
                      </li>

                      {data?.user?.role_id === 10 && (
                        <li className="mobile-user-row">
                          <span className="text-uppercase">
                            {t("referralCode")}:
                          </span>
                          <span className="text-end">
                            {data?.orgUser?.referral_code}
                          </span>
                        </li>
                      )}
                    </ul>
                  </Col>

                  {/* Residence */}
                  <Col xs="12" lg="4">
                    <h6 className="text-silverBlue">
                      {t("signup.residence")}
                    </h6>

                    <ul className="list-unstyled user-data my-3">
                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("deliveryAddress")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.address}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("provinceDelivery")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.province}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("cityDelivery")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.city}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("postalAddress")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.postcode}
                        </span>
                      </li>
                    </ul>
                  </Col>

                  {/* Domicile */}
                  <Col xs="12" lg="4">
                    <h6 className="text-silverBlue">
                      {t("signup.domicile")}
                    </h6>

                    <ul className="list-unstyled user-data my-3">
                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("deliveryAddress")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.address_home}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("provinceDelivery")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.province_home}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("cityDelivery")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.city_home}
                        </span>
                      </li>

                      <li className="mobile-user-row">
                        <span className="text-uppercase">
                          {t("postalAddress")}:
                        </span>
                        <span className="text-end">
                          {data?.orgUser?.postcode_home}
                        </span>
                      </li>
                    </ul>
                  </Col>

                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col md="12" className="mt-3">
            <Card bg="dark1 text-light">
              <Card.Body>
                <Tab.Container defaultActiveKey="first">
                  <Row>
                    <Col sm={3}>
                      <Nav variant="pills" className="mb-2 flex-row flex-md-column flex-nowrap text-nowrap overflow-auto">
                        <Nav.Item>
                          <Nav.Link eventKey="first">
                            {t("personalDetails")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="second">
                            {t("bankDetails")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="third">
                            {t("contactDetails")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="fourth">
                            {t("signup.residence")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="fifth">
                            {t("domicile")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="sixth">
                            {t("selectLanguge")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="seventh">
                            {t("requestNewKey")}
                          </Nav.Link>
                        </Nav.Item>
                      </Nav>
                    </Col>
                    <Col sm={9}>
                      <Tab.Content>
                        <Tab.Pane eventKey="first">
                          <PersonalDetails data={data} refetch={refetch} isLoading={isLoading} error={error} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="second">
                          <BankDetails refetch={refetch} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="third">
                          <ContactDetails data={data} refetch={refetch} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="fourth">
                          <ResidentialDetails data={data} refetch={refetch} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="fifth">
                          <DomicileDetails data={data} refetch={refetch} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="sixth">
                          <SelectLanguage data={data} refetch={refetch} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="seventh">
                          <RequestNewKey />
                        </Tab.Pane>
                      </Tab.Content>
                    </Col>
                  </Row>
                </Tab.Container>
              </Card.Body>
            </Card>
          </Col>
          {data?.plan.length > 0 && (
            <Col className="mt-3" md="12">
              <HeliorPlanDetails plans={data?.plan} />
            </Col>
          )}
          {data?.heliorOne.length > 0 && (
            <Col className="mt-3" md="12">
              <HOneDetails plans={data?.heliorOne} />
            </Col>
          )}
        </>
      )}
    </Row>
  );
};

export default Profile;
